import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../config';

const CapabilityAssessment = ({ onLogout, apiBaseUrl }) => {
  const navigate = useNavigate();
  
  // 15 security capability categories
  const categories = [
    {
      id: 'identity_access',
      name: 'Identity & Access Management',
      icon: '🔐',
      description: 'User authentication, authorization, and access controls',
      questions: [
        'How are user identities managed and authenticated?',
        'What access control mechanisms are in place?',
        'How is privileged access managed and monitored?'
      ]
    },
    {
      id: 'network_security',
      name: 'Network Security',
      icon: '🌐',
      description: 'Network architecture, segmentation, and perimeter defense',
      questions: [
        'How is the network segmented and protected?',
        'What perimeter security controls are deployed?',
        'How is network traffic monitored?'
      ]
    },
    {
      id: 'data_protection',
      name: 'Data Protection',
      icon: '🛡️',
      description: 'Data classification, encryption, and loss prevention',
      questions: [
        'How is sensitive data classified and inventoried?',
        'What encryption is used for data at rest and in transit?',
        'What data loss prevention controls exist?'
      ]
    },
    {
      id: 'incident_response',
      name: 'Incident Response',
      icon: '🚨',
      description: 'Detection, response, and recovery from security incidents',
      questions: [
        'Is there a formal incident response plan?',
        'How are security incidents detected and triaged?',
        'What is the process for incident recovery?'
      ]
    },
    {
      id: 'vulnerability_management',
      name: 'Vulnerability Management',
      icon: '🔍',
      description: 'Vulnerability scanning, assessment, and remediation',
      questions: [
        'How often are vulnerability scans conducted?',
        'What is the process for prioritizing vulnerabilities?',
        'How quickly are critical vulnerabilities patched?'
      ]
    },
    {
      id: 'security_awareness',
      name: 'Security Awareness & Training',
      icon: '🎓',
      description: 'Employee education and security culture',
      questions: [
        'What security training is provided to employees?',
        'How often is training conducted?',
        'How is security awareness measured?'
      ]
    },
    {
      id: 'asset_management',
      name: 'Asset Management',
      icon: '📦',
      description: 'IT asset inventory and lifecycle management',
      questions: [
        'Is there a complete inventory of IT assets?',
        'How are assets tracked throughout their lifecycle?',
        'What controls exist for unauthorized devices?'
      ]
    },
    {
      id: 'application_security',
      name: 'Application Security',
      icon: '💻',
      description: 'Secure development and application testing',
      questions: [
        'Are applications developed using secure coding practices?',
        'What security testing is performed before deployment?',
        'How are application vulnerabilities managed?'
      ]
    },
    {
      id: 'cloud_security',
      name: 'Cloud Security',
      icon: '☁️',
      description: 'Cloud infrastructure and service security',
      questions: [
        'How are cloud workloads secured?',
        'What controls govern cloud access and configuration?',
        'How is cloud security posture monitored?'
      ]
    },
    {
      id: 'third_party_risk',
      name: 'Third-Party Risk Management',
      icon: '🤝',
      description: 'Vendor security assessment and oversight',
      questions: [
        'How are third-party vendors assessed for security?',
        'What contractual security requirements exist?',
        'How is ongoing vendor security monitored?'
      ]
    },
    {
      id: 'backup_recovery',
      name: 'Backup & Recovery',
      icon: '💾',
      description: 'Data backup and business continuity',
      questions: [
        'How frequently are backups performed?',
        'Are backups tested for recoverability?',
        'What is the RTO/RPO for critical systems?'
      ]
    },
    {
      id: 'logging_monitoring',
      name: 'Logging & Monitoring',
      icon: '📊',
      description: 'Security event logging and analysis',
      questions: [
        'What security events are logged and retained?',
        'How are logs analyzed for security threats?',
        'What alerting mechanisms are in place?'
      ]
    },
    {
      id: 'physical_security',
      name: 'Physical Security',
      icon: '🏢',
      description: 'Physical access controls and environmental security',
      questions: [
        'What physical access controls protect facilities?',
        'How are visitors and contractors managed?',
        'What environmental controls protect equipment?'
      ]
    },
    {
      id: 'compliance_governance',
      name: 'Compliance & Governance',
      icon: '⚖️',
      description: 'Regulatory compliance and security policies',
      questions: [
        'What security policies and standards are in place?',
        'How is compliance with regulations ensured?',
        'How often are policies reviewed and updated?'
      ]
    },
    {
      id: 'endpoint_security',
      name: 'Endpoint Security',
      icon: '🖥️',
      description: 'Workstation and mobile device protection',
      questions: [
        'What endpoint protection tools are deployed?',
        'How are mobile devices secured?',
        'What controls exist for remote access?'
      ]
    }
  ];

  // Maturity level definitions
  const maturityLevels = [
    {
      level: 1,
      name: 'Initial',
      description: 'Ad-hoc processes, reactive approach, inconsistent implementation',
      color: 'red'
    },
    {
      level: 2,
      name: 'Developing',
      description: 'Some documented processes, basic controls in place, limited automation',
      color: 'orange'
    },
    {
      level: 3,
      name: 'Defined',
      description: 'Documented processes, consistent implementation, regular reviews',
      color: 'yellow'
    },
    {
      level: 4,
      name: 'Managed',
      description: 'Measured and monitored processes, data-driven improvements, automation',
      color: 'blue'
    },
    {
      level: 5,
      name: 'Optimized',
      description: 'Continuous improvement, industry-leading practices, full automation',
      color: 'green'
    }
  ];

  const [currentStep, setCurrentStep] = useState('overview'); // overview, category, results
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [evidence, setEvidence] = useState({});
  const [notes, setNotes] = useState({});
  const [assessmentId, setAssessmentId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Load saved assessment on mount
  useEffect(() => {
    if (apiBaseUrl) {
      loadSavedAssessment();
    }
  }, [apiBaseUrl]);

  const loadSavedAssessment = async () => {
    try {
      const token = localStorage.getItem('ciso_access_token');
      const response = await fetch(`${apiBaseUrl}/assessments/current`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.assessment) {
          setAssessmentId(data.assessment.id);
          setResponses(data.assessment.responses || {});
          setNotes(data.assessment.notes || {});
        }
      }
    } catch (error) {
      console.error('Error loading assessment:', error);
    }
  };

  const saveProgress = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('ciso_access_token');
      const customerId = JSON.parse(localStorage.getItem('ciso_user') || '{}').customer_id || '';
    
      const response = await fetch(`${config.apiUrl}/api/assessments/save`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          customer_id: customerId,
          assessment_id: assessmentId,
          responses,
          notes,
          completed_categories: Object.keys(responses).length,
          total_categories: categories.length
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAssessmentId(data.assessment_id);
      
        localStorage.setItem('customer_id', customerId);
        localStorage.setItem('current_assessment_id', data.assessment_id.toString());
      
        console.log('Assessment saved:', data.assessment_id);
      }
    } catch (error) {
      console.error('Error saving assessment:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleResponse = (categoryId, score, targetScore) => {
    setResponses(prev => ({
      ...prev,
      [categoryId]: { score, target: targetScore }
    }));
  };

  const handleNote = (categoryId, note) => {
    setNotes(prev => ({
      ...prev,
      [categoryId]: note
    }));
  };

  const nextCategory = () => {
    saveProgress();
    if (currentCategoryIndex < categories.length - 1) {
      setCurrentCategoryIndex(prev => prev + 1);
    } else {
      setCurrentStep('results');
    }
  };

  const previousCategory = () => {
    if (currentCategoryIndex > 0) {
      setCurrentCategoryIndex(prev => prev - 1);
    }
  };

  const startAssessment = () => {
    setCurrentStep('category');
    setCurrentCategoryIndex(0);
  };

  const goToCategory = (index) => {
    setCurrentCategoryIndex(index);
    setCurrentStep('category');
  };

  const calculateOverallScore = () => {
    const scores = Object.values(responses).map(r => r.score);
    if (scores.length === 0) return 0;
    return (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1);
  };

  const getProgressPercentage = () => {
    return Math.round((Object.keys(responses).length / categories.length) * 100);
  };

  const getLevelColor = (level) => {
    const colors = {
      1: 'bg-red-100 text-red-800 border-red-300',
      2: 'bg-orange-100 text-orange-800 border-orange-300',
      3: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      4: 'bg-blue-100 text-blue-800 border-blue-300',
      5: 'bg-emerald-100 text-emerald-800 border-emerald-300'
    };
    return colors[level] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const currentCategory = categories[currentCategoryIndex];
  const currentResponse = responses[currentCategory?.id] || { score: null, target: null };

  // Overview Screen
  if (currentStep === 'overview') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
        <header className="bg-white border-b border-slate-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Capability Assessment</h1>
                <p className="text-sm text-slate-600 mt-1">Evaluate your security maturity across 15 categories</p>
              </div>
              <div className="flex items-center gap-4">
                {saving && <span className="text-sm text-slate-600">Saving...</span>}
                {onLogout && (
                  <button onClick={onLogout} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200">
                    Logout
                  </button>
                )}
              </div>
            </div>
          </div>
        </header>

        <div className="max-w-6xl mx-auto px-6 py-8">
          {/* Progress Card */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Assessment Progress</h2>
                <p className="text-sm text-slate-600 mt-1">
                  {Object.keys(responses).length} of {categories.length} categories completed
                </p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-blue-600">{getProgressPercentage()}%</div>
                <div className="text-sm text-slate-600">Complete</div>
              </div>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-3">
              <div
                className="bg-blue-600 h-3 rounded-full transition-all duration-500"
                style={{ width: `${getProgressPercentage()}%` }}
              ></div>
            </div>
          </div>

          {/* Category Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {categories.map((category, index) => {
              const isCompleted = responses[category.id];
              return (
                <button
                  key={category.id}
                  onClick={() => goToCategory(index)}
                  className={`text-left p-6 rounded-xl border-2 transition-all hover:shadow-md ${
                    isCompleted
                      ? 'bg-white border-emerald-300 hover:border-emerald-400'
                      : 'bg-white border-slate-200 hover:border-blue-300'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <span className="text-3xl">{category.icon}</span>
                    {isCompleted && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                        ✓ Done
                      </span>
                    )}
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">{category.name}</h3>
                  <p className="text-sm text-slate-600">{category.description}</p>
                  {isCompleted && (
                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">Current:</span>
                        <span className="font-bold text-slate-900">{responses[category.id].score}/5</span>
                      </div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              onClick={startAssessment}
              className="flex-1 px-6 py-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-medium text-lg"
            >
              {Object.keys(responses).length > 0 ? 'Continue Assessment' : 'Start Assessment'}
            </button>
            {Object.keys(responses).length === categories.length && (
              <button
                onClick={() => setCurrentStep('results')}
                className="flex-1 px-6 py-4 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 font-medium text-lg"
              >
                View Results
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Category Assessment Screen
  if (currentStep === 'category' && currentCategory) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
        <header className="bg-white border-b border-slate-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setCurrentStep('overview')}
                  className="p-2 hover:bg-slate-100 rounded-lg"
                >
                  ← Back
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">{currentCategory.name}</h1>
                  <p className="text-sm text-slate-600 mt-1">
                    Category {currentCategoryIndex + 1} of {categories.length}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {saving && <span className="text-sm text-slate-600">Saving...</span>}
                {onLogout && (
                  <button onClick={onLogout} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200">
                    Logout
                  </button>
                )}
              </div>
            </div>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-6 py-8">
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="w-full bg-slate-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentCategoryIndex + 1) / categories.length) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Category Icon and Description */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 mb-6">
            <div className="flex items-start gap-4 mb-6">
              <span className="text-5xl">{currentCategory.icon}</span>
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">{currentCategory.name}</h2>
                <p className="text-slate-600">{currentCategory.description}</p>
              </div>
            </div>

            {/* Key Questions */}
            <div className="border-t border-slate-200 pt-6">
              <h3 className="font-bold text-slate-900 mb-3">Key Assessment Questions:</h3>
              <ul className="space-y-2">
                {currentCategory.questions.map((q, i) => (
                  <li key={i} className="flex items-start gap-2 text-slate-700">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>{q}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Current Maturity Level */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 mb-6">
            <h3 className="font-bold text-slate-900 mb-4">Current Maturity Level</h3>
            <p className="text-sm text-slate-600 mb-4">Select the level that best describes your current state:</p>

            <div className="space-y-3">
              {maturityLevels.map((level) => (
                <button
                  key={level.level}
                  onClick={() => handleResponse(currentCategory.id, level.level, currentResponse.target || level.level)}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                    currentResponse.score === level.level
                      ? getLevelColor(level.level) + ' border-opacity-100'
                      : 'bg-white border-slate-200 hover:border-slate-300'
                  }`}
                  style={{ color: '#1e293b' }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-bold text-lg" style={{ color: '#1e293b' }}>Level {level.level}</span>
                        <span className="font-bold" style={{ color: '#1e293b' }}>{level.name}</span>
                      </div>
                      <p className="text-sm" style={{ color: '#475569' }}>{level.description}</p>
                    </div>
                    {currentResponse.score === level.level && (
                      <span className="text-xl ml-4">✓</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Target Maturity Level */}
          {currentResponse.score !== null && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 mb-6">
              <h3 className="font-bold text-slate-900 mb-4">Target Maturity Level</h3>
              <p className="text-sm text-slate-600 mb-4">What level do you want to achieve?</p>

              <div className="grid grid-cols-5 gap-2">
                {maturityLevels.map((level) => (
                  <button
                    key={level.level}
                    onClick={() => handleResponse(currentCategory.id, currentResponse.score, level.level)}
                    className={`p-3 rounded-lg border-2 text-center transition-all ${
                      currentResponse.target === level.level
                        ? getLevelColor(level.level) + ' border-opacity-100'
                        : 'bg-white border-slate-200 hover:border-slate-300'
                    }`}
                    style={{ color: '#1e293b' }}
                  >
                    <div className="font-bold text-lg mb-1" style={{ color: '#1e293b' }}>Level {level.level}</div>
                    <div className="text-xs" style={{ color: '#475569' }}>{level.name}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {currentResponse.score !== null && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 mb-6">
              <h3 className="font-bold text-slate-900 mb-4">Notes & Evidence</h3>
              <p className="text-sm text-slate-600 mb-4">
                Document your rationale, evidence, or additional context:
              </p>
              <textarea
                value={notes[currentCategory.id] || ''}
                onChange={(e) => handleNote(currentCategory.id, e.target.value)}
                placeholder="Add notes about current capabilities, planned improvements, evidence, etc..."
                className="w-full h-32 px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ color: '#1e293b' }}
              />
            </div>
          )}

          {/* Navigation */}
          <div className="flex gap-4">
            <button
              onClick={previousCategory}
              disabled={currentCategoryIndex === 0}
              className="px-6 py-3 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              ← Previous
            </button>
            <button
              onClick={nextCategory}
              disabled={currentResponse.score === null || currentResponse.target === null}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {currentCategoryIndex === categories.length - 1 ? 'Complete Assessment →' : 'Next Category →'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Results Screen
  if (currentStep === 'results') {
    const overallScore = calculateOverallScore();
    const categoryScores = categories.map(cat => ({
      ...cat,
      ...responses[cat.id]
    }));

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
        <header className="bg-white border-b border-slate-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setCurrentStep('overview')}
                  className="p-2 hover:bg-slate-100 rounded-lg"
                >
                  ← Back
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">Assessment Results</h1>
                  <p className="text-sm text-slate-600 mt-1">Your security maturity assessment</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {onLogout && (
                  <button onClick={onLogout} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200">
                    Logout
                  </button>
                )}
              </div>
            </div>
          </div>
        </header>

        <div className="max-w-6xl mx-auto px-6 py-8">
          {/* Overall Score */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 mb-8">
            <div className="text-center">
              <h2 className="text-lg font-bold text-slate-700 mb-2">Overall Security Maturity</h2>
              <div className="text-6xl font-bold text-blue-600 mb-4">{overallScore}</div>
              <p className="text-slate-600">
                Based on {Object.keys(responses).length} category assessments
              </p>
            </div>
          </div>

          {/* Category Results */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
            <h2 className="text-lg font-bold text-slate-900 mb-6">Category Breakdown</h2>
            
            <div className="space-y-4">
              {categoryScores.map((cat) => (
                <div key={cat.id} className="border-b border-slate-100 pb-4 last:border-0">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{cat.icon}</span>
                      <span className="font-medium text-slate-900">{cat.name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-slate-600">Current:</span>
                      <span className="font-bold text-slate-900">{cat.score}/5</span>
                      <span className="text-sm text-slate-400">→</span>
                      <span className="text-sm text-slate-600">Target:</span>
                      <span className="font-bold text-blue-600">{cat.target}/5</span>
                    </div>
                  </div>
                  <div className="relative w-full bg-slate-100 rounded-full h-2.5">
                    <div 
                      className="absolute top-0 h-2.5 w-0.5 bg-slate-400" 
                      style={{ left: `${(cat.target / 5) * 100}%` }}
                    ></div>
                    <div 
                      className={`h-2.5 rounded-full transition-all duration-500 ${
                        cat.score >= cat.target ? 'bg-emerald-500' : 
                        cat.score >= 3 ? 'bg-blue-500' :
                        cat.score >= 2 ? 'bg-orange-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${(cat.score / 5) * 100}%` }}
                    ></div>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-slate-500">
                      Gap: {(cat.target - cat.score).toFixed(1)} levels
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Gap Analysis Button */}
          <div className="mt-8 text-center">
            <button
              onClick={() => navigate('/gap-analysis')}
              className="bg-blue-600 text-white px-8 py-4 rounded-lg hover:bg-blue-700 font-semibold text-lg shadow-lg hover:shadow-xl transition-all"
            >
              📊 View Gap Analysis & Recommendations
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default CapabilityAssessment;

